import React, {
  useState,
  useMemo,
  useLayoutEffect,
  useRef,
  useEffect,
  useCallback,
} from "react";
import classnames from "classnames";

import Tippy, { TippyProps } from "@tippyjs/react";
import "tippy.js/dist/tippy.css";

import { Loader, TextInput, FormControlDropdown } from "..";

import useDropdown from "../../hooks/useDropdown";
import useElementSize from "../../hooks/useElementSize";
import useUpdateEffect from "../../hooks/useUpdateEffect";
import useIntersectionObserver from "../../hooks/useIntersectionObserver";

import styles from "./AutoComplete.module.scss";

export interface Value {
  value: string | number;
  label: string;
  description?: string;
  iconComponent?: React.ElementType;
}

export interface Props {
  id: string;
  options?: Value[];
  value?: Value;
  placeholder: string;
  width?: number;
  title?: React.ReactNode;
  wrapperClassName?: string;
  tooltipClassName?: string;
  inputClassName?: string;
  required?: boolean;
  isAutoFocused?: boolean;
  onBlur?: () => void;
  onChange: (
    event: React.SyntheticEvent,
    value: string | number | null,
    label: string
  ) => void;
  onTextChange?: (event: React.SyntheticEvent, value: string) => void;
  isError?: boolean;
  errorText?: string;
  hintText?: string;
  disabled?: boolean;
  limit?: number;
  isLoading?: boolean;
  isEmptyStateVisible?: boolean;
  filterOption?: (option: Value, query: string) => boolean;
}

const defaultProps = {
  value: null,
  options: [],
  onBlur: () => {},
};

const popperOptions: TippyProps["popperOptions"] = {
  strategy: "fixed",
  modifiers: [
    {
      name: "preventOverflow",
    },
  ],
};

const AutoComplete = (props: Props) => {
  const {
    id,
    title,
    value = defaultProps.value,
    options = defaultProps.options,
    wrapperClassName,
    inputClassName,
    tooltipClassName,
    width,
    required,
    onChange,
    onTextChange,
    placeholder,
    isAutoFocused = false,
    onBlur = defaultProps.onBlur,
    limit = 50,
    isLoading,
    isEmptyStateVisible,
    filterOption,
    ...rest
  } = props;
  const { value: currentValue = null, label: currentLabel = "" } = value || {};
  const ref = useRef<HTMLDivElement>(null);
  const optionsWrapperRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tippyRef = useRef<any>(null);
  const [textValue, setTextValue] = useState(currentLabel);
  const { width: minWidth } = useElementSize(inputRef);

  const isInputVisible = useIntersectionObserver({
    ref: inputRef,
  });

  const filteredOptions = useMemo(() => {
    const searchValue = textValue.trim().toLowerCase();

    return options
      .filter((item) => {
        if (filterOption) {
          return filterOption(item, searchValue);
        }

        const label = item.label.trim().toLowerCase();

        return label.includes(searchValue);
      })
      .slice(0, limit);
  }, [options, textValue, limit, filterOption]);

  const updateTippy = useCallback(() => {
    setTimeout(() => {
      tippyRef?.current?._tippy?.popperInstance?.update();
    });
  }, [tippyRef]);

  useEffect(() => {
    window.addEventListener("resize", updateTippy);

    return () => {
      window.removeEventListener("resize", updateTippy);
    };
  }, [updateTippy]);

  const onValueChange = (
    e: React.SyntheticEvent,
    v: string | number | null,
    l: string
  ) => {
    onChange(e, v, l);

    setTextValue(l);
  };

  const onTextValueChange = (e: React.SyntheticEvent, v: string) => {
    setTextValue(v);
    onTextChange?.(e, v);

    if (!v) {
      onChange(e, null, "");
    }
  };

  const {
    selectedIdx,
    isDropdownOpened,
    onKeyDown,
    changeDropdownState,
  } = useDropdown(ref, optionsWrapperRef, filteredOptions, onValueChange);

  const onInputFocus = () => changeDropdownState(true);

  useLayoutEffect(() => {
    if (isAutoFocused) {
      onInputFocus();
    }
  }, [isAutoFocused]); // eslint-disable-line

  useEffect(() => {
    if (isInputVisible === false) {
      changeDropdownState(false);
    }
  }, [isInputVisible]); // eslint-disable-line

  useUpdateEffect(() => {
    if (isDropdownOpened) {
      inputRef.current?.focus();
    } else {
      onBlur();
      setTextValue(currentLabel);
      inputRef.current?.blur();
    }
  }, [isDropdownOpened]);

  useUpdateEffect(() => {
    setTextValue(currentLabel);
  }, [currentValue, currentLabel]);

  const getOption = ({
    label,
    description,
    iconComponent: IconComponent,
  }: Value) => (
    <>
      <span className={styles.autoCompleteOptionText}>
        <span>{label}</span>
        {!!description && (
          <span className={styles.autoCompleteOptionDescription}>
            {description}
          </span>
        )}
      </span>
      {IconComponent && (
        <IconComponent width={16} height={16} fillColor="#B5BDC2" />
      )}
    </>
  );

  const tooltipContent = (
    <FormControlDropdown<Value>
      wrapperRef={optionsWrapperRef}
      value={value?.value}
      selectedIdx={selectedIdx}
      options={filteredOptions}
      onChange={(e, v, l) => {
        onChange(e, v, l);
        setTextValue(l);
        changeDropdownState(false);
      }}
      getOption={getOption}
      minWidth={minWidth}
      optionClassName={styles.autoCompleteOptionItem}
    />
  );

  return (
    <div
      ref={ref}
      style={{ width }}
      className={classnames(styles.autoCompleteWrapper, wrapperClassName)}
    >
      <Tippy
        interactive
        ref={tippyRef}
        arrow={false}
        offset={[0, 2]}
        animation={false}
        placement="bottom-start"
        content={tooltipContent}
        popperOptions={popperOptions}
        className={classnames(styles.autoCompleteTooltip, tooltipClassName)}
        visible={
          isDropdownOpened &&
          (!!filteredOptions.length || (!isLoading && isEmptyStateVisible))
        }
      >
        <TextInput
          id={id}
          type="search"
          title={title}
          ref={inputRef}
          value={textValue}
          placeholder={placeholder}
          inputClassName={classnames(styles.autoCompleteInput, inputClassName)}
          onChange={onTextValueChange}
          onFocus={onInputFocus}
          onKeyDown={onKeyDown}
          required={required}
          inputIconContent={
            isLoading && <Loader fontSize={20} className={styles.loader} />
          }
          {...rest}
        />
      </Tippy>
    </div>
  );
};

export default AutoComplete;
